"use client";

import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import type { EmblaOptionsType } from "embla-carousel";
import { DotButton, useDotButton } from "./EmblaCarouselDotButton";
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from "./EmblaCarouselArrowButtons";
import Icon from "../../atoms/Icon";
import { ICONS } from "../../variables/icons";
import useBreakpoint from "../../hooks/useBreakpoint";

type CustomEmblaCarouselProps = {
  slides: JSX.Element[];
  options?: EmblaOptionsType;
  classNames?: string;
  autoPlayInterval: number;
  enableAutoplay: boolean;
  componentName?: string;
  setSelectedIndex?: (index: number) => void;
};

interface PlayPauseButtonProps {
  isPlaying: boolean;
  onToggle: (action: "play" | "pause") => void;
}

const PlayPauseButton = memo<PlayPauseButtonProps>(
  ({ isPlaying, onToggle }) => (
    <button
      onClick={() => onToggle(isPlaying ? "pause" : "play")}
      className="cursor-pointer rounded-full w-[36px] h-[36px] text-transparent bg-ecolab-blue"
      aria-label={isPlaying ? "Pause" : "Play"}
    >
      <Icon icon={isPlaying ? ICONS.PAUSE : ICONS.PLAY} />
    </button>
  ),
);
PlayPauseButton.displayName = "PlayPauseButton";

const isCardOrEventVariant = (componentName?: string): boolean =>
  componentName === "CardCarousel" || componentName === "EventSlider";

const CustomEmblaCarousel = memo<CustomEmblaCarouselProps>((props) => {
  const {
    slides,
    options,
    classNames,
    autoPlayInterval,
    enableAutoplay,
    componentName,
    setSelectedIndex,
  } = props;

  const { isMobile, isTablet, isDesktop } = useBreakpoint();

  const isCardOrEvent = isCardOrEventVariant(componentName);

  const plugins =
    componentName === "CardCarousel" || !enableAutoplay
      ? []
      : [
          Autoplay({
            playOnInit: autoPlayInterval > 0,
            delay: autoPlayInterval,
            stopOnInteraction: isDesktop,
            stopOnMouseEnter: true,
            stopOnFocusIn: true,
          }),
        ];

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      ...options,
      skipSnaps: false,
      dragFree: false,
      containScroll: "trimSnaps",
    },
    plugins,
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const [autoplayIsPlaying, setAutoplayIsPlaying] = useState(
    autoPlayInterval > 0,
  );
  const slideRefs = useRef<HTMLDivElement[]>([]);
  const [isUserPaused, setIsUserPaused] = useState(false);
  const [heightsSynced, setHeightsSynced] = useState(false);

  // Equalise the inner `.setHeight` element across all slides so the
  // carousel doesn't jump when slides have different content lengths.
  const syncSlideHeights = useCallback(() => {
    if (!slideRefs.current.length || heightsSynced) return;

    requestAnimationFrame(() => {
      const measuredSlides = slideRefs.current.filter(Boolean);
      if (!measuredSlides.length) return;

      // Reset heights so we measure intrinsic heights, not previously-applied ones.
      measuredSlides.forEach((slide) => {
        const el = slide.querySelector(".setHeight") as HTMLElement | null;
        if (el) el.style.height = "auto";
      });

      const heights = measuredSlides.map((slide) => {
        const el = slide.querySelector(".setHeight") as HTMLElement | null;
        return el ? el.scrollHeight : 0;
      });

      const maxHeight = Math.max(...heights);

      measuredSlides.forEach((slide) => {
        const el = slide.querySelector(".setHeight") as HTMLElement | null;
        if (el) el.style.height = `${maxHeight}px`;
      });

      setHeightsSynced(true);
    });
  }, [heightsSynced]);

  const handleAutoPlay = useCallback(
    (action: "play" | "pause") => {
      const autoplay = emblaApi?.plugins()?.autoplay;
      if (!autoplay) return;

      if (action === "pause") {
        autoplay.stop();
        setAutoplayIsPlaying(false);
        setIsUserPaused(true);
      } else {
        autoplay.play();
        setAutoplayIsPlaying(true);
        setIsUserPaused(false);
      }
    },
    [emblaApi],
  );

  // Initial sync + re-sync on resize.
  useEffect(() => {
    if (!emblaApi) return;

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setHeightsSynced(false);
        syncSlideHeights();
      }, 100);
    };

    syncSlideHeights();
    emblaApi.on("resize", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      emblaApi.off("resize", handleResize);
    };
  }, [emblaApi, syncSlideHeights]);

  // Pause on hover (desktop only).
  useEffect(() => {
    if (isMobile || !emblaApi) return;

    const autoplay = emblaApi.plugins()?.autoplay;
    if (!autoplay) return;

    const node = emblaApi.containerNode();
    let hoverTimeout: ReturnType<typeof setTimeout>;

    const onMouseEnter = () => {
      clearTimeout(hoverTimeout);
      autoplay.stop();
    };

    const onMouseLeave = () => {
      // Small delay before resuming to prevent flickering on quick mouseovers.
      hoverTimeout = setTimeout(() => {
        if (!isUserPaused) autoplay.play();
      }, 100);
    };

    node.addEventListener("mouseenter", onMouseEnter, { passive: true });
    node.addEventListener("mouseleave", onMouseLeave, { passive: true });

    return () => {
      clearTimeout(hoverTimeout);
      node.removeEventListener("mouseenter", onMouseEnter);
      node.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [emblaApi, isUserPaused, isMobile]);

  // Bubble active slide up to the consumer.
  useEffect(() => {
    setSelectedIndex?.(selectedIndex);
  }, [selectedIndex, setSelectedIndex]);

  if (!slides?.length) return null;

  const showDots = (() => {
    if (!isCardOrEvent) return true;
    if (isDesktop) return scrollSnaps.length > 4;
    if (isTablet) return scrollSnaps.length > 3;
    if (isMobile) return scrollSnaps.length > 1;
    return true;
  })();

  const showCardSideArrows =
    options?.active &&
    slides.length > 4 &&
    (isTablet || isDesktop) &&
    isCardOrEvent;

  const isHeroDarkVariant =
    componentName === "DefaultHeroCarouselLeft" ||
    componentName === "HeroCarouselNoBox";

  const bottomPositionClass = isHeroDarkVariant ? "bottom-8" : "bottom-4";

  const dotsContainerClass = isCardOrEvent
    ? "flex bg-white mx-auto px-[10px] h-[33px] rounded-[20px] leading-[22.8571px]"
    : `flex pr-2 rounded-full gap-3 absolute left-6 bg-dark-blue ${
        bottomPositionClass
      }`;

  const backgroundColorClass = isCardOrEvent ? "!bg-ecolab-blue" : "!bg-teal";

  return (
    <section className="md:w-full sm:w-160 xs:w-full">
      {showCardSideArrows && (
        <div className="grid grid-cols-2 gap-1 items-center">
          <PrevButton
            className="bg-ecolab-blue text-white leading-0"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            tabIndex={0}
          />
        </div>
      )}
      <div
        className={`overflow-hidden ${isCardOrEvent ? "mb-[10px] pb-5" : ""}`}
        ref={emblaRef}
      >
        <div className="flex">
          {slides.map((slide, index) => (
            <div
              className={`grow-0 shrink-0 basis-full min-w-0 ${classNames || ""}`}
              key={`${String(slide.key).slice(0, 10)}-${index}`}
              ref={(el) => {
                if (el) slideRefs.current[index] = el;
              }}
              role="group"
              aria-label={`Slide ${index + 1} of ${slides.length}`}
              tabIndex={-1}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      {options?.active && slides.length > 1 && (
        <div className={isCardOrEvent ? "flex justify-center" : "relative"}>
          {showCardSideArrows && (
            <div className="grid grid-cols-2 gap-1 items-center">
              <NextButton
                className="bg-ecolab-blue text-white leading-0"
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
                tabIndex={0}
              />
            </div>
          )}

          {(showDots || (enableAutoplay && autoPlayInterval > 0)) && (
            <div className={dotsContainerClass}>
              {enableAutoplay && autoPlayInterval > 0 && (
                <PlayPauseButton
                  isPlaying={autoplayIsPlaying}
                  onToggle={handleAutoPlay}
                />
              )}

              {showDots && (
                <div className="flex items-center">
                  {scrollSnaps.map((_, index) => {
                    const baseDotClass = isCardOrEvent
                      ? "m-[6px] w-[12px] h-[12px] text-[0] indent-[-999px] border-none rounded-[7px] cursor-pointer bg-dots-gray leading-[0]"
                      : "appearance-none no-underline touch-manipulation rounded-[50%] w-3 h-3 cursor-pointer border-[dark-blue] p-0 m-[6px] bg-white";
                    const activeDotClass =
                      index === selectedIndex ? backgroundColorClass : "";
                    return (
                      <DotButton
                        key={`dot-${scrollSnaps[index]}`}
                        onClick={() => onDotButtonClick(index)}
                        className={`flex items-center justify-center ${baseDotClass} ${activeDotClass}`}
                        aria-label={`Go to slide ${index + 1}${
                          index === selectedIndex ? " (current slide)" : ""
                        }`}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
});
CustomEmblaCarousel.displayName = "CustomEmblaCarousel";

export default CustomEmblaCarousel;
